import { useSyncExternalStore } from 'react'
import type { ItemView, WorkbenchChange, WorkbenchCounts } from '@/components/Workbench/chartContext'

/**
 * Bridge store between the Workbench chart and the AI chat (both live under
 * BotDetail but in separate subtrees).
 *
 * The Workbench keeps the latest chart context STAGED whenever something the
 * user drew differs from what the AI last saw (rebuilt on every chart edit,
 * incrementally diffed against the last send). The chat turns a staged context
 * into the composer's "Chart updated" tab, whose Send to AI sends it with
 * whatever is typed in the box; with nothing staged, a typed message goes alone.
 *
 * Staging has to happen Workbench-side rather than at send time because the
 * Workbench — and with it the chart engine — unmounts on the Backtest and
 * Optimise tabs. The last staged context survives there, so the tab (and a
 * send from it) still works from any tab.
 */

export interface StagedChart {
  label: string
  text: string
  /** Context items keyed by stable id — becomes aiPush.sentItems on commit. */
  items: Record<string, string>
  /** Views of the drawn items — becomes aiPush.sentViews on commit. */
  views: Record<string, ItemView>
  /** The AI has never seen this symbol's chart ("Send chart to AI"). */
  first: boolean
  /** What the person sees listed: drawn elements added / edited / removed. */
  changes: WorkbenchChange[]
  counts: WorkbenchCounts
  omitted: number
  /** The symbol the context was built from — chart state and AI bookkeeping
   * are per-symbol, so the commit must fold into THAT symbol's diff base. */
  symbol: string
  /** When this context was built (ms). */
  atMs: number
}

export interface ChartPushState {
  bid: string | null
  /** Changes waiting for Send to AI; null once sent or when nothing differs. */
  staged: StagedChart | null
  /** Commits this session (>0 ⇒ "has pushed"); seeded from persisted state. */
  pushCount: number
  /** Last commit — lets the Workbench reconcile a send that happened while it was unmounted. */
  lastCommit: {
    sentAt: number
    sentItems: Record<string, string>
    sentViews: Record<string, ItemView>
    symbol: string
  } | null
  /** True once the Workbench seeded from the persisted workbench state. */
  seeded: boolean
}

const fresh = (bid: string | null): ChartPushState => ({
  bid,
  staged: null,
  pushCount: 0,
  lastCommit: null,
  seeded: false,
})

// One state per bot. A send resolves only when the AI's turn is over, which can be
// minutes after the user moved on to another strategy — its commit must land on
// ITS bot, never reset the one now on screen.
const states = new Map<string, ChartPushState>()
const listeners = new Set<(s: ChartPushState) => void>()
// Stable per-bid empty snapshots, so the hook's getSnapshot stays pure and
// referentially stable for a bot the store has not touched yet.
const freshSnaps = new Map<string, ChartPushState>()
// Mounted Workbenches per bot. Not rendered state: only a send's commit asks (BotDetail), to
// know whether a Workbench will fold it into the saved blob or it has to be persisted for it.
const attached = new Map<string, number>()

function emit(s: ChartPushState) {
  listeners.forEach((fn) => fn(s))
}

function read(bid: string): ChartPushState {
  let s = states.get(bid)
  if (!s) {
    s = fresh(bid)
    states.set(bid, s)
  }
  return s
}

function write(bid: string, s: ChartPushState) {
  states.set(bid, s)
  emit(s)
}

/** Make sure the bot has a state (a fresh one the first time it is touched). */
function ensure(bid: string) {
  read(bid)
}

/** Pure read for render paths: never mutates, never emits. */
function snapshotFor(bid: string): ChartPushState {
  const s = states.get(bid)
  if (s) return s
  let snap = freshSnaps.get(bid)
  if (!snap) {
    snap = fresh(bid)
    freshSnaps.set(bid, snap)
  }
  return snap
}

export const chartPush = {
  /** Read for event handlers. */
  get(bid: string): ChartPushState {
    return read(bid)
  },

  ensure,

  subscribe(fn: (s: ChartPushState) => void): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  /** Seed the staged changes and push count from the persisted workbench state,
   * so a reload keeps offering the changes the user had not sent yet. */
  seedIfFresh(bid: string, seed: { hasPushes: boolean; staged?: StagedChart | null }) {
    const state = read(bid)
    if (state.seeded || state.staged || state.pushCount > 0) return
    write(bid, {
      ...state,
      staged: seed.staged ?? null,
      pushCount: seed.hasPushes ? 1 : 0,
      seeded: true,
    })
  },

  /** Offer the freshly built changes on the composer, replacing any prior. */
  stage(bid: string, input: StagedChart): StagedChart {
    const staged: StagedChart = { ...input }
    write(bid, { ...read(bid), staged })
    return staged
  },

  /** Nothing left to send (the chart matches what the AI already has). */
  clearStaged(bid: string) {
    const state = read(bid)
    if (!state.staged) return
    write(bid, { ...state, staged: null })
  },

  /**
   * `sent` reached the AI. It becomes the diff base — `sent`, not whatever is
   * staged now: an edit made while the request was in flight has already
   * replaced the staged snapshot, and committing THAT would mark changes the AI
   * never saw as seen. A newer snapshot stays staged for the Workbench to
   * rebuild against the new base.
   */
  commit(bid: string, sent: StagedChart | null = states.get(bid)?.staged ?? null): StagedChart | null {
    if (!sent) return null
    const state = read(bid)
    const sentAt = Date.now()
    write(bid, {
      ...state,
      staged: state.staged === sent ? null : state.staged,
      pushCount: state.pushCount + 1,
      lastCommit: { sentAt, sentItems: sent.items, sentViews: sent.views, symbol: sent.symbol },
    })
    return sent
  },

  /** The bot's Workbench is mounted until the returned detach runs. */
  attachWorkbench(bid: string): () => void {
    attached.set(bid, (attached.get(bid) ?? 0) + 1)
    let detached = false
    return () => {
      if (detached) return
      detached = true
      const n = (attached.get(bid) ?? 1) - 1
      if (n > 0) attached.set(bid, n)
      else attached.delete(bid)
    }
  },

  /** Whether a Workbench is mounted for the bot — it then persists its own commits. */
  isWorkbenchAttached(bid: string): boolean {
    return (attached.get(bid) ?? 0) > 0
  },

  /** Forget every bot's state (tests). */
  reset() {
    states.clear()
    freshSnaps.clear()
    attached.clear()
  },
}

export function useChartPush(bid: string): ChartPushState {
  return useSyncExternalStore(chartPush.subscribe, () => snapshotFor(bid))
}
